import { motion } from "framer-motion";
import {
  Map, Link2, TrendingUp, CloudSun, Brain, Tv, Bell, Gem,
  ChevronDown
} from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useIsMobile } from "@/hooks/use-mobile";

import screenshotMap from "@/assets/screenshots/map-threats.png";
import screenshotChain from "@/assets/screenshots/signal-chain.png";
import screenshotMarkets from "@/assets/screenshots/markets.png";
import screenshotResources from "@/assets/screenshots/resources.png";
import screenshotClimate from "@/assets/screenshots/climate-seismic.png";
import screenshotIntel from "@/assets/screenshots/intel-analysis.png";
import screenshotLive from "@/assets/screenshots/live-feeds.png";
import screenshotAlerts from "@/assets/screenshots/alerts-safety.png";

const CATEGORIES = [
  {
    id: "map",
    icon: Map,
    label: "Map & Threats",
    screenshot: screenshotMap,
    tools: [
      { name: "Live Conflict Map", desc: "Real-time global event mapping with clustering", free: true },
      { name: "Conflict Timeline", desc: "Chronological event visualization", free: true },
      { name: "Threats Near Me", desc: "Proximity-based threat detection within 500km", free: false },
      { name: "Strategic Posture", desc: "DEFCON-style global threat level assessment", free: false },
      { name: "Personal Safety Radar", desc: "Individual risk scoring based on location", free: false },
      { name: "Travel Safety Check", desc: "Country-level travel risk assessment", free: false },
      { name: "Refugee Tracker", desc: "UNHCR displacement data monitoring", free: false },
      { name: "Country Instability Index", desc: "Composite political/military instability scoring", free: false },
    ],
  },
  {
    id: "chain",
    icon: Link2,
    label: "Signal Chain",
    screenshot: screenshotChain,
    tools: [
      { name: "Signal Chain Feed", desc: "Classified intelligence feed with severity scoring", free: false },
      { name: "Arms Inventory Monitor", desc: "Global arms transfer tracking via SIPRI", free: false },
      { name: "Cyber Attack Monitor", desc: "Real-time CISA KEV vulnerability tracking", free: false },
      { name: "Nuclear Threat Monitor", desc: "Nuclear arsenal status and activity monitoring", free: false },
      { name: "Airline Intelligence", desc: "FIR status and airspace closure tracking", free: false },
      { name: "Tech Readiness Index", desc: "Infrastructure and defense capability scoring", free: false },
    ],
  },
  {
    id: "markets",
    icon: TrendingUp,
    label: "Markets",
    screenshot: screenshotMarkets,
    tools: [
      { name: "Market Radar", desc: "Oil, gold, wheat — conflict-correlated commodity tracking", free: false },
      { name: "Crypto Monitor", desc: "Real-time prices and stablecoin de-peg alerts", free: false },
      { name: "Prediction Markets", desc: "Geopolitical odds via Polymarket", free: false },
      { name: "Conflict Economic Impact", desc: "AI-generated economic impact analysis", free: false },
    ],
  },
  {
    id: "resources",
    icon: Gem,
    label: "Resources",
    screenshot: screenshotResources,
    tools: [
      { name: "Essential Goods Scarcity", desc: "Price tracking for food, fuel, and essentials", free: false },
      { name: "Critical Materials Monitor", desc: "Rare earth and strategic material supply tracking", free: false },
      { name: "Supply Chain Monitor", desc: "Disruption risk for 8 maritime chokepoints", free: false },
      { name: "World Bank Data", desc: "GDP and military expenditure for 18 countries", free: false },
    ],
  },
  {
    id: "climate",
    icon: CloudSun,
    label: "Climate & Seismic",
    screenshot: screenshotClimate,
    tools: [
      { name: "Earthquake Monitor", desc: "Real-time USGS seismic event tracking", free: true },
      { name: "Climate Anomalies", desc: "NASA EONET wildfire and storm detection", free: true },
    ],
  },
  {
    id: "intel",
    icon: Brain,
    label: "Intel & Analysis",
    screenshot: screenshotIntel,
    tools: [
      { name: "Daily Intelligence Brief", desc: "AI-generated 24h analytical summary", free: false },
      { name: "Weekly Report", desc: "Comprehensive weekly situation report", free: false },
      { name: "AI Intel Feed", desc: "AI-generated situational assessments", free: false },
      { name: "Historical Conflict Comparison", desc: "AI-powered conflict pattern analysis", free: false },
      { name: "Sanctions Tracker", desc: "International sanctions monitoring", free: false },
      { name: "Flight Risk Index", desc: "Airspace closure probability scoring", free: false },
      { name: "Missile Early Warning", desc: "Salvo detection and impact radius estimates", free: false },
      { name: "Israel Sirens", desc: "Real-time Red Alert tracking", free: false },
    ],
  },
  {
    id: "live",
    icon: Tv,
    label: "Live Feeds",
    screenshot: screenshotLive,
    tools: [
      { name: "Live News TV", desc: "24/7 streaming from major news networks", free: true },
      { name: "Live Webcams", desc: "Strategic location camera feeds", free: true },
      { name: "Breaking News Feed", desc: "Aggregated real-time news from 100+ sources", free: true },
    ],
  },
  {
    id: "alerts",
    icon: Bell,
    label: "Alerts & Safety",
    screenshot: screenshotAlerts,
    tools: [
      { name: "Smart Alerts", desc: "Multi-channel notifications (email, Telegram, push)", free: false },
      { name: "Safe Routes", desc: "Evacuation corridor analysis", free: false },
      { name: "Country Emergency Guide", desc: "Protocols for 14 high-risk nations", free: false },
      { name: "Panic Alerts (SOS)", desc: "Rapid coordinate-sharing emergency system", free: false },
    ],
  },
];

const MOBILE_INITIAL_VISIBLE = 2;

type TabId = "map" | "chain" | "markets" | "resources" | "climate" | "intel" | "live" | "alerts";

interface Props {
  onSelectTab?: (tabId: TabId) => void;
}

const PremiumToolsShowcase = ({ onSelectTab }: Props) => {
  const isMobile = useIsMobile();
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const totalTools = CATEGORIES.reduce((sum, c) => sum + c.tools.length, 0);

  return (
    <section className="py-6 md:py-12 border-b border-border">
      <div className="container mx-auto px-3 md:px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-4 md:mb-6 text-center"
        >
          <h2 className="text-lg md:text-2xl font-black text-foreground">
            {totalTools} Intelligence Tools — Full Catalog
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Everything included — real-time push alerts for your region, AI analysis, and all tools below. No add-ons.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-2.5 md:gap-4">
          {CATEGORIES.map((cat, ci) => {
            const Icon = cat.icon;
            const isExpanded = expandedCategories[cat.id];
            const visibleTools =
              isMobile && !isExpanded
                ? cat.tools.slice(0, MOBILE_INITIAL_VISIBLE)
                : cat.tools;
            const hasMore = isMobile && cat.tools.length > MOBILE_INITIAL_VISIBLE;

            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: ci * 0.05 }}
                className="rounded-xl border border-border bg-card p-2.5 md:p-4 flex flex-col"
              >
                {/* Screenshot slot */}
                {cat.screenshot && (
                  <div className="mb-2 rounded-lg overflow-hidden relative aspect-video bg-muted">
                    <img
                      src={cat.screenshot}
                      alt={`${cat.label} preview`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/60 to-transparent" />
                  </div>
                )}

                {/* Category header */}
                <button
                  onClick={() => {
                    if (onSelectTab) {
                      onSelectTab(cat.id as TabId);
                      setTimeout(() => {
                        const tabBar = document.querySelector("[data-tab-bar]");
                        tabBar?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }, 100);
                    }
                  }}
                  className="flex items-center gap-1.5 md:gap-2 mb-2 md:mb-3 group/header w-full text-left"
                >
                  <div className="flex h-6 w-6 md:h-8 md:w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover/header:bg-primary/20 transition-colors">
                    <Icon className="h-3 w-3 md:h-4 md:w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs md:text-sm font-bold text-foreground group-hover/header:text-primary transition-colors leading-tight block truncate">
                      {cat.label}
                    </span>
                    <span className="text-[9px] md:text-[10px] text-muted-foreground">{cat.tools.length} tools</span>
                  </div>
                </button>

                {/* Tool list */}
                <ul className="space-y-1.5 md:space-y-2 flex-1">
                  {visibleTools.map((tool) => (
                    <li key={tool.name} className="flex items-start gap-1.5">
                      <div className="mt-0.5 shrink-0">
                        <Badge
                          variant={tool.free ? "secondary" : "default"}
                          className="text-[7px] md:text-[8px] px-1 md:px-1.5 py-0 leading-4"
                        >
                          {tool.free ? "FREE" : "PRO"}
                        </Badge>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] md:text-xs font-semibold text-foreground leading-tight truncate">
                          {tool.name}
                        </p>
                        <p className="hidden md:block text-[10px] text-muted-foreground leading-tight">
                          {tool.desc}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>

                {/* Expand toggle */}
                {hasMore && !isExpanded && (
                  <button
                    onClick={() => toggleCategory(cat.id)}
                    className="mt-1.5 flex items-center gap-0.5 text-[10px] text-primary hover:underline"
                  >
                    <ChevronDown className="h-2.5 w-2.5" />
                    +{cat.tools.length - MOBILE_INITIAL_VISIBLE} more
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PremiumToolsShowcase;
